import { z } from 'zod';
import { languageCodeSchema } from './facility';
import { productAttachmentSchema } from './products';

// Title is stored as a JSONB array of per-language entries — mirrors the
// legacy ProductTitleDto[] shape used across the codebase. Output schema is
// permissive on the language code (the DB may hold legacy values outside the
// current `languageCodeSchema` enum); the write schema enforces it.
const ticketVenueTitleEntryOutputSchema = z.object({
  languageCode: z.string(),
  text: z.string(),
  default: z.boolean().optional(),
});

const ticketVenueTitleEntryInputSchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string().min(1),
  default: z.boolean().optional(),
});

// Price is a single object: one currency for the whole venue + N category
// rows. Mirrors the legacy VenuePriceDto.
const ticketVenuePriceRowSchema = z.object({
  category: z.string(),
  price: z.number().nullable().optional(),
});

export const ticketVenuePriceSchema = z.object({
  currency: z.string(),
  prices: z.array(ticketVenuePriceRowSchema),
});

export type TicketVenuePrice = z.infer<typeof ticketVenuePriceSchema>;

// [longitude, latitude] tuple — same convention as
// `productLocationPointTupleSchema` so the service can pass it directly to
// `ST_SetSRID(ST_MakePoint(lng, lat), 4326)`.
export const ticketVenueLocationPointSchema = z.tuple([z.number(), z.number()]);

export type TicketVenueLocationPoint = z.infer<
  typeof ticketVenueLocationPointSchema
>;

// The venue's associated supplier as it goes OUT on the wire. Only the id is
// stored (`venue.supplier_id`); `name` is read through `crm.v_supplier` — a
// UNION of supplier companies and their contacts — so a CRM rename shows up
// without a backfill. `name` is empty when the id no longer resolves there.
export const ticketVenueSupplierSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export type TicketVenueSupplier = z.infer<typeof ticketVenueSupplierSchema>;

export const ticketVenueItemSchema = z.object({
  id: z.uuid(),
  title: z.array(ticketVenueTitleEntryOutputSchema),
  price: ticketVenuePriceSchema.nullable(),
  locationPoint: ticketVenueLocationPointSchema.nullable(),
  formattedAddress: z.string().nullable(),
  attachments: z.array(productAttachmentSchema).nullable(),
  // XT-538 — the org's Regular/Priority split. Sole input to the /operations
  // "Priority venues" card, which matches `itinerary_item.details.venue`
  // against the titles of flagged venues.
  isPriority: z.boolean(),
  // Pre-fills the Supplier field on any ticket assigned to this venue.
  supplier: ticketVenueSupplierSchema.nullable(),
});

export type TicketVenueItem = z.infer<typeof ticketVenueItemSchema>;

export const createTicketVenueInput = z.object({
  title: z.array(ticketVenueTitleEntryInputSchema).min(1),
  price: ticketVenuePriceSchema.optional(),
  locationPoint: ticketVenueLocationPointSchema.optional(),
  formattedAddress: z.string().optional(),
  attachments: z.array(productAttachmentSchema).optional(),
  // Omitted means Regular — the column defaults to false.
  isPriority: z.boolean().optional(),
  // Writes carry the id alone; the name is never persisted.
  supplierId: z.uuid().optional(),
});

export type CreateTicketVenueInput = z.infer<typeof createTicketVenueInput>;

export const updateTicketVenueInput = z.object({
  id: z.uuid(),
  title: z.array(ticketVenueTitleEntryInputSchema).min(1).optional(),
  price: ticketVenuePriceSchema.nullable().optional(),
  locationPoint: ticketVenueLocationPointSchema.nullable().optional(),
  formattedAddress: z.string().nullable().optional(),
  attachments: z.array(productAttachmentSchema).nullable().optional(),
  // Not nullable: Regular is `false`, not "unset". The admin table's inline
  // toggle sends this key alone.
  isPriority: z.boolean().optional(),
  // Nullable: clearing the picker detaches the supplier from the venue.
  supplierId: z.uuid().nullable().optional(),
});

export type UpdateTicketVenueInput = z.infer<typeof updateTicketVenueInput>;

export const findTicketVenuesInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  title: z.string().optional(),
  formattedAddress: z.string().optional(),
});

export type FindTicketVenuesInput = z.infer<typeof findTicketVenuesInput>;

export const findTicketVenuesResultSchema = z.object({
  data: z.array(ticketVenueItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindTicketVenuesResult = z.infer<
  typeof findTicketVenuesResultSchema
>;

export const deleteTicketVenueInput = z.object({ id: z.uuid() });

export type DeleteTicketVenueInput = z.infer<typeof deleteTicketVenueInput>;

export const deleteTicketVenueResultSchema = z.object({ id: z.uuid() });

export type DeleteTicketVenueResult = z.infer<
  typeof deleteTicketVenueResultSchema
>;
